import { useId } from 'react';
import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WERT UND EINHEIT (Auftrag 17.09.2026, Abschnitt 15)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Besonders wichtig für TGA-Werte: Wert und Einheit klar unterscheiden,
 *    z. B. Volumenstrom [ 85.000 ] [m³/h], Temperatur [ 36,0 ] [°C],
 *    Leistung [ 1.100 ] [kW]. Einheiten nicht willkürlich im Placeholder
 *    verstecken."
 *
 * Zwei Bausteine:
 *   `WertEinheit`      - Anzeige (Tabellenzelle, Kennwerttafel, Kennzahl)
 *   `WertEinheitFeld`  - Eingabe (Formular)
 *
 * WARUM DIE EINHEIT EIN EIGENES ELEMENT IST
 * -----------------------------------------
 * Im Bestand steht die Einheit an drei verschiedenen Orten: in der
 * Spaltenüberschrift („Bedarf in kW"), hinter der Zahl im selben Textknoten
 * („199,4 Pa") und im Platzhalter eines Eingabefeldes. Das dritte ist der
 * Fehler: Der Platzhalter verschwindet, sobald jemand tippt - genau dann,
 * wenn die Einheit gebraucht wird. Hier ist sie ein eigenes Element neben
 * dem Feld und bleibt stehen.
 *
 * ZAHLENFORMAT
 * ------------
 * Deutsche Schreibweise (`de-DE`): Punkt gruppiert, Komma trennt die
 * Nachkommastelle. Das ist AUSSCHLIESSLICH die Anzeige - an die API und in
 * Rechnungen geht nie eine gruppierte Zahl (Lehre „Zahlen im KI-Prompt":
 * ein Tausenderpunkt macht jeden Millimeterwert ab 1000 um Faktor 1000
 * falsch). `leseZahl` ist der Rückweg und kennt beide Schreibweisen.
 */

/** Zeichen für „kein Wert vorhanden" - nie eine 0, die wäre eine Aussage. */
export const OHNE_WERT = '—';

/**
 * Zahl in deutscher Schreibweise.
 *
 * `nachkomma` erzwingt genau so viele Nachkommastellen (36 → „36,0"). Ohne
 * Angabe entscheidet die Zahl selbst: bis zu drei Stellen, keine
 * überflüssigen Nullen.
 */
export function formatiereZahl(
  wert: number | null | undefined,
  nachkomma?: number,
): string {
  if (wert === null || wert === undefined) return OHNE_WERT;
  if (typeof wert !== 'number' || !Number.isFinite(wert)) return OHNE_WERT;

  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: nachkomma ?? 0,
    maximumFractionDigits: nachkomma ?? 3,
  });
}

/**
 * Getippten Text als Zahl lesen. Kennt die deutsche Schreibweise
 * („1.100,5"), die englische („1,100.5") und die aus einer Tabelle kopierte
 * Mischform („36.0"). Gibt `null` zurück, wenn nichts Zahlhaftes dasteht -
 * NICHT 0, denn „leer" und „null" sind fachlich zweierlei.
 */
export function leseZahl(text: string | null | undefined): number | null {
  if (text === null || text === undefined) return null;
  const roh = String(text).trim().replace(/\s/g, '');
  if (roh === '') return null;

  const hatKomma = roh.includes(',');
  const hatPunkt = roh.includes('.');

  let normiert: string;
  if (hatKomma && hatPunkt) {
    // Das zuletzt stehende Zeichen trennt die Nachkommastelle, das andere
    // gruppiert: „1.100,5" deutsch, „1,100.5" englisch.
    const kommaZuletzt = roh.lastIndexOf(',') > roh.lastIndexOf('.');
    normiert = kommaZuletzt
      ? roh.replace(/\./g, '').replace(',', '.')
      : roh.replace(/,/g, '');
  } else if (hatKomma) {
    normiert = roh.replace(/,/g, '.');
  } else if (hatPunkt) {
    // Nur Punkte. Genau drei Ziffern hinter JEDEM Punkt heißt gruppiert
    // („85.000", „1.100.000"); alles andere ist eine Nachkommastelle
    // („36.0", „0.75").
    const gruppiert = /^-?\d{1,3}(\.\d{3})+$/.test(roh);
    normiert = gruppiert ? roh.replace(/\./g, '') : roh;
  } else {
    normiert = roh;
  }

  const zahl = Number(normiert);
  return Number.isFinite(zahl) ? zahl : null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ANZEIGE
   ═══════════════════════════════════════════════════════════════════════════ */

export interface WertEinheitProps {
  /** Zahl oder fertiger Text. `null`/`undefined` ergibt „—". */
  wert: number | string | null | undefined;
  einheit?: string;
  /** Erzwungene Nachkommastellen; wirkt nur bei einer Zahl. */
  nachkomma?: number;
  /** Beschriftung links davor, z. B. in einer Kennwerttafel. */
  beschriftung?: ReactNode;
  /** Größere, halbfette Darstellung - für Kennzahlkarten. */
  gross?: boolean;
  /** Rechtsbündig - Vorgabe in Zahlenspalten. */
  rechts?: boolean;
  /** Erläuterung beim Überfahren. */
  titel?: string;
  className?: string;
}

export function WertEinheit({
  wert, einheit, nachkomma, beschriftung, gross, rechts, titel, className,
}: WertEinheitProps) {
  const text = typeof wert === 'number' || wert === null || wert === undefined
    ? formatiereZahl(wert as number | null | undefined, nachkomma)
    : String(wert);

  const fehlt = text === OHNE_WERT;
  const klassen = ['m-wert'];
  if (gross) klassen.push('m-wert-gross');
  if (rechts) klassen.push('m-wert-rechts');
  if (className) klassen.push(className);

  return (
    <span
      className={klassen.join(' ')}
      title={titel}
      // Zusammenhängend für die Vorlesehilfe: sonst liest sie Zahl und
      // Einheit als zwei Angaben ohne Bezug.
      aria-label={fehlt || !einheit ? undefined : `${text} ${einheit}`}
    >
      {beschriftung && <span className="m-wert-beschriftung">{beschriftung}</span>}
      <span className={`m-wert-zahl${fehlt ? ' m-wert-fehlt' : ''}`}>{text}</span>
      {einheit && !fehlt && (
        <span className="m-wert-einheit" aria-hidden="true">{einheit}</span>
      )}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   EINGABE
   ═══════════════════════════════════════════════════════════════════════════ */

export interface WertEinheitFeldProps {
  beschriftung: string;
  wert: number | null | undefined;
  beiAenderung?: (wert: number | null) => void;
  einheit?: string;
  /** Nachkommastellen der Anzeige, sobald das Feld den Fokus verliert. */
  nachkomma?: number;
  /** Erläuterung unter dem Feld (nicht im Platzhalter). */
  hinweis?: string;
  /** Fehlertext; setzt zusätzlich `aria-invalid`. */
  fehler?: string;
  pflicht?: boolean;
  deaktiviert?: boolean;
  nurLesen?: boolean;
  min?: number;
  max?: number;
  /** Platzhalter - die Einheit gehört hier NICHT hinein (Abschnitt 15). */
  platzhalter?: string;
  id?: string;
  /** Feldbreite im Gitter einer `FormSection`. */
  breite?: 'voll' | 'halb' | 'drittel';
}

export function WertEinheitFeld({
  beschriftung, wert, beiAenderung, einheit, nachkomma, hinweis, fehler,
  pflicht, deaktiviert, nurLesen, min, max, platzhalter, id, breite = 'halb',
}: WertEinheitFeldProps) {
  const eigeneId = useId();
  const feldId = id ?? `we-${eigeneId}`;
  const hinweisId = `${feldId}-hinweis`;
  const fehlerId = `${feldId}-fehler`;

  const beschreibung = [
    hinweis ? hinweisId : null,
    fehler ? fehlerId : null,
  ].filter(Boolean).join(' ');

  return (
    <div className={`m-feld m-feld-${breite}${fehler ? ' m-feld-fehlerhaft' : ''}`}>
      <label className="m-feld-beschriftung" htmlFor={feldId}>
        {beschriftung}
        {pflicht && <span className="m-feld-pflicht" title="Pflichtangabe">*</span>}
      </label>

      <div className="m-feld-wertzeile">
        <input
          id={feldId}
          // `text` statt `number`: Das Zahlenfeld des Browsers lehnt das
          // deutsche Komma ab und verschluckt getippte Gruppierungspunkte.
          // Gelesen wird mit `leseZahl`, geprüft mit min/max von Hand.
          type="text"
          inputMode="decimal"
          className="m-feld-eingabe m-feld-zahl"
          value={wert === null || wert === undefined
            ? ''
            : formatiereZahl(wert, nachkomma)}
          placeholder={platzhalter}
          disabled={deaktiviert}
          readOnly={nurLesen}
          required={pflicht}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={beschreibung || undefined}
          onChange={(e) => beiAenderung?.(leseZahl(e.target.value))}
        />
        {einheit && (
          <span className="m-feld-einheit" aria-hidden="true">{einheit}</span>
        )}
      </div>

      {(min !== undefined || max !== undefined) && !hinweis && !fehler && (
        <span className="m-feld-hinweis" id={hinweisId}>
          {min !== undefined && max !== undefined
            ? `zulässig ${formatiereZahl(min, nachkomma)} bis ${formatiereZahl(max, nachkomma)}`
            : min !== undefined
              ? `mindestens ${formatiereZahl(min, nachkomma)}`
              : `höchstens ${formatiereZahl(max ?? null, nachkomma)}`}
          {einheit ? ` ${einheit}` : ''}
        </span>
      )}
      {hinweis && !fehler && (
        <span className="m-feld-hinweis" id={hinweisId}>{hinweis}</span>
      )}
      {fehler && (
        <span className="m-feld-fehler" id={fehlerId} role="alert">
          <span aria-hidden="true">! </span>{fehler}
        </span>
      )}
    </div>
  );
}
